import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { getRecord } from './helpers'
import { MissingRequiredDataError } from './missing-required-data-error'
import { QUERY_KEYS } from './query-keys'
import { useQuoteQuery } from './quotes'

export const accountSchema = z.object({
  accountid: z.string(),
  address1_line1: z.string({
    message: 'Address line 1 is required. Please set a complete address on the Dynamics account record.',
  }),
  address1_line2: z.string().nullable(),
  address1_line3: z.string().nullable(),
  address1_city: z.string({
    message: 'City is required. Please set a complete address on the Dynamics account record.',
  }),
  address1_stateorprovince: z.string({
    message: 'State is required. Please set a complete address on the Dynamics account record.',
  }),
  address1_postalcode: z.string({
    message: 'Zip code is required. Please set a complete address on the Dynamics account record.',
  }),
  address1_country: z.string().nullable(),
  msdyn_taxexempt: z.boolean().nullable().optional(),
  msdyn_taxexemptnumber: z.string().nullable().optional(),
  msdyn_salestaxcode: z
    .object({
      msdyn_taxcodeid: z.string(),
      msdyn_taxrate: z.number(),
      msdyn_name: z.string(),
      statecode: z.number(),
    })
    .nullable()
    .optional(),
})

export type AccountData = z.infer<typeof accountSchema>

export function useAccountQuery() {
  const { data: quote } = useQuoteQuery()
  const accountId = quote?._customerid_value
  return useQuery({
    queryKey: QUERY_KEYS.account,
    enabled: !!accountId,
    queryFn: async () => {
      if (!accountId) {
        throw new Error('Related account not found on the quote.')
      }
      const account = await getRecord(
        'accounts',
        accountId,
        '?$select=accountid,name,address1_line1,address1_line2,address1_line3,address1_city,address1_stateorprovince,address1_postalcode,address1_country,msdyn_taxexempt,msdyn_taxexemptnumber&$expand=msdyn_salestaxcode($select=msdyn_taxcodeid,msdyn_taxrate,msdyn_name,statecode)'
      )
      const result = accountSchema.safeParse(account)
      if (!result.success) {
        throw new MissingRequiredDataError('accounts', [
          { id: accountId, raw: account as Record<string, unknown>, issues: result.error.issues },
        ])
      }
      return result.data
    },
  })
}

export const contactSchema = z.object({
  contactid: z.string(),
  fullname: z.string({
    message: 'Full name is required. Please set a full name on the Dynamics contact record.',
  }),
  firstname: z.string().nullable().optional(),
  dsy_companyname: z.string().nullable().optional(),
  emailaddress1: z.string().nullable().optional(),
})

export type ContactData = z.infer<typeof contactSchema>

export function usePrimaryContactQuery() {
  const { data: quote } = useQuoteQuery()
  const primaryContactId = quote?._dsy_contact_value
  return useQuery({
    queryKey: QUERY_KEYS.primaryContact,
    enabled: !!primaryContactId,
    queryFn: async () => {
      if (!primaryContactId) {
        throw new Error('Related contact not found on the quote.')
      }
      const contact = await getRecord(
        'contacts',
        primaryContactId,
        // firstname/lastname prefill the fix-missing-data form (fullname is platform-computed)
        '?$select=contactid,fullname,firstname,lastname,dsy_companyname,emailaddress1'
      )
      const result = contactSchema.safeParse(contact)
      if (!result.success) {
        throw new MissingRequiredDataError('contacts', [
          { id: primaryContactId, raw: contact as Record<string, unknown>, issues: result.error.issues },
        ])
      }
      return result.data
    },
  })
}
